import type { SipEventPort, SipStoragePort, SipTimerPort } from './contracts';

const LEASE_STALE_MS = 6_000;
const LEASE_HEARTBEAT_MS = 1_500;
const PASSIVE_RETRY_MS = 1_000;
const TAB_SESSION_KEY = 'intez:sip-tab-id:v4';

type LeaseRecord = {
  tabId?: string;
  timestamp?: number;
};

type LeaseRead = {
  available: boolean;
  record: LeaseRecord;
};

export interface SipTabLeaseDeps {
  storage: SipStoragePort;
  sessionStorage: SipStoragePort;
  events: SipEventPort;
  timers: SipTimerPort;
  now: () => number;
  createId: () => string;
  canAcquire: () => boolean;
  onAcquired: () => void;
  onLost: () => void;
  onRestore: () => void;
}

export class SipTabLease {
  private readonly storage: SipStoragePort;
  private readonly events: SipEventPort;
  private readonly timers: SipTimerPort;
  private readonly now: () => number;
  private readonly canAcquire: () => boolean;
  private readonly onAcquired: () => void;
  private readonly onLost: () => void;
  private readonly onRestore: () => void;
  private readonly tabId: string;
  private readonly onPageHide = () => this.release();
  private readonly onBeforeUnload = () => this.release();
  private readonly onPageShow = (event: any) => {
    if (!this.disposed && event?.persisted) this.onRestore();
  };
  private readonly onStorage = (event: any) => {
    if (
      this.disposed
      || !this.passiveRetryWanted
      || this.ownsLease
      || !this.leaseKey
      || event?.key !== this.leaseKey
    ) {
      return;
    }
    this.startPassiveRetry();
  };
  private leaseKey = '';
  private heartbeatTimer: number | null = null;
  private passiveRetryTimer: number | null = null;
  private passiveRetryWanted = false;
  private ownsLease = false;
  private lossReported = false;
  private disposed = false;

  constructor(deps: SipTabLeaseDeps) {
    this.storage = deps.storage;
    this.events = deps.events;
    this.timers = deps.timers;
    this.now = deps.now;
    this.canAcquire = deps.canAcquire;
    this.onAcquired = deps.onAcquired;
    this.onLost = deps.onLost;
    this.onRestore = deps.onRestore;
    this.tabId = this.createTabId(deps.sessionStorage, deps.createId);

    this.events.addEventListener('pagehide', this.onPageHide);
    this.events.addEventListener('beforeunload', this.onBeforeUnload);
    this.events.addEventListener('pageshow', this.onPageShow);
    this.events.addEventListener('storage', this.onStorage);
  }

  configureOwner(ownerId: string): void {
    if (this.disposed) return;
    const owner = String(ownerId || '').trim();
    const nextKey = owner ? `intez:sip-primary:${owner}` : '';
    if (nextKey === this.leaseKey) return;

    this.stopTimers();
    this.releaseOwnedLease();
    this.leaseKey = nextKey;
    this.ownsLease = false;
  }

  claim(): boolean {
    if (this.disposed || !this.canAcquire()) return false;
    if (!this.leaseKey) {
      this.ownsLease = true;
      return true;
    }

    const current = this.readLease();
    if (!current.available) {
      this.ownsLease = true;
      return true;
    }

    const { tabId, timestamp } = current.record;
    const freshForeign = Boolean(
      tabId
      && tabId !== this.tabId
      && typeof timestamp === 'number'
      && Number.isFinite(timestamp)
      && this.now() - timestamp <= LEASE_STALE_MS,
    );
    if (freshForeign) {
      this.ownsLease = false;
      return false;
    }

    return this.writeAndConfirm();
  }

  takeOver(): boolean {
    this.stopPassiveRetry();
    if (this.disposed || !this.canAcquire()) return false;
    if (!this.leaseKey) {
      this.ownsLease = true;
      return true;
    }
    return this.writeAndConfirm();
  }

  startHeartbeat(): void {
    this.stopHeartbeat();
    this.stopPassiveRetry();
    if (this.disposed || !this.leaseKey) return;

    this.lossReported = false;
    const active = this.heartbeat();
    if (!active || this.disposed) return;

    this.heartbeatTimer = this.timers.setInterval(() => {
      this.heartbeat();
    }, LEASE_HEARTBEAT_MS);
  }

  startPassiveRetry(): void {
    this.stopPassiveRetry();
    if (this.disposed || !this.leaseKey || !this.canAcquire()) return;

    this.passiveRetryWanted = true;
    if (this.retryAcquire()) return;

    this.passiveRetryTimer = this.timers.setInterval(() => {
      if (this.disposed || !this.passiveRetryWanted || !this.canAcquire()) {
        this.stopPassiveRetry();
        return;
      }
      this.retryAcquire();
    }, PASSIVE_RETRY_MS);
  }

  stopTimers(): void {
    this.stopHeartbeat();
    this.stopPassiveRetry();
  }

  release(): void {
    if (this.disposed) return;
    this.releaseOwnedLease();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.stopTimers();
    this.releaseOwnedLease();
    this.events.removeEventListener('pagehide', this.onPageHide);
    this.events.removeEventListener('beforeunload', this.onBeforeUnload);
    this.events.removeEventListener('pageshow', this.onPageShow);
    this.events.removeEventListener('storage', this.onStorage);
  }

  private createTabId(
    sessionStorage: SipStoragePort,
    createId: () => string,
  ): string {
    const generated = String(createId() || '').trim()
      || `${this.now()}-${Math.random()}`;

    try {
      sessionStorage.setItem(TAB_SESSION_KEY, generated);
    } catch {
      // Изолированный идентификатор остаётся доступен без sessionStorage.
    }

    return generated;
  }

  private readLease(): LeaseRead {
    try {
      const serialized = this.storage.getItem(this.leaseKey);
      if (!serialized) return { available: true, record: {} };
      const parsed = JSON.parse(serialized);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return { available: false, record: {} };
      }
      return { available: true, record: parsed as LeaseRecord };
    } catch {
      return { available: false, record: {} };
    }
  }

  private writeAndConfirm(): boolean {
    const record = { tabId: this.tabId, timestamp: this.now() };

    try {
      this.storage.setItem(this.leaseKey, JSON.stringify(record));
      const confirmed = this.readLease();
      if (!confirmed.available) {
        this.ownsLease = true;
        return true;
      }
      this.ownsLease = confirmed.record.tabId === this.tabId;
      return this.ownsLease;
    } catch {
      this.ownsLease = true;
      return true;
    }
  }

  private heartbeat(): boolean {
    if (this.disposed || !this.leaseKey) return false;
    const current = this.readLease();

    if (!current.available) return true;
    if (current.record.tabId && current.record.tabId !== this.tabId) {
      this.ownsLease = false;
      this.stopHeartbeat();
      if (!this.lossReported && !this.disposed) {
        this.lossReported = true;
        this.onLost();
      }
      return false;
    }

    try {
      this.storage.setItem(this.leaseKey, JSON.stringify({
        tabId: this.tabId,
        timestamp: this.now(),
      }));
      this.ownsLease = true;
    } catch {
      // В одновкладочном fallback телефон продолжает работать.
    }
    return true;
  }

  private retryAcquire(): boolean {
    if (!this.claim()) return false;
    this.stopPassiveRetry();
    if (!this.disposed) this.onAcquired();
    return true;
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      this.timers.clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private stopPassiveRetry(): void {
    this.passiveRetryWanted = false;
    if (this.passiveRetryTimer !== null) {
      this.timers.clearInterval(this.passiveRetryTimer);
      this.passiveRetryTimer = null;
    }
  }

  private releaseOwnedLease(): void {
    if (!this.leaseKey) return;
    const current = this.readLease();
    if (current.available && current.record.tabId === this.tabId) {
      try {
        this.storage.removeItem(this.leaseKey);
      } catch {
        // Хранилище уже недоступно — освобождать больше нечего.
      }
    }
    this.ownsLease = false;
  }
}
